import type { EnumCallApplicationTypeApi } from '@ingenico/api-sdk-device-management-private';
import { enumCallApplicationType } from '@ingenico/api-sdk-device-management-private';
import type {
  DeviceCallResourceReadV2Api,
  DeviceResourceNetworkDataApi,
  EnumCallStatusApi,
  GetDeviceCallByIdV2QueryResponseApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { enumCallStatus } from '@ingenico/api-sdk-device-management-private-v2';
import type {
  PhysicalDeviceListResponseApi,
  PhysicalDeviceResourceReadApi,
} from '@ingenico/api-sdk-provisioning-public';
import { camelCase, isEqual, toNumber, toString } from 'lodash-es';
import type {
  CallDetailsDto,
  DeviceCallLogDetail,
  DeviceCallLogHeaderDto,
  HardwareDetailsDto,
  LabelledColored,
} from '../../../model/callLogs.model';
import { dashString } from '../../../utils/dashString.util';

/* -------------------------------------------------------------------------- */
/*                                 HEADER DTO                                 */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                                HEADER STATUS                               */
/* -------------------------------------------------------------------------- */

function getLabel(status: EnumCallStatusApi): string {
  const suffixOfStatusLoweredCase = toString(status).replaceAll('STATUS_', '').toLowerCase();

  return suffixOfStatusLoweredCase;
}

function getColor(status: EnumCallStatusApi): 'failed' | 'info' | 'neutral' | 'success' | 'warning' {
  switch (status) {
    case enumCallStatus.SUCCESS: {
      return 'success';
    }

    case enumCallStatus.FAILURE:
    case enumCallStatus.REJECTED: {
      return 'failed';
    }

    case enumCallStatus.SUSPENDED: {
      return 'info';
    }

    default: {
      return 'neutral';
    }
  }
}

export function mapDeviceCallStatus(status: EnumCallStatusApi): LabelledColored {
  const label = getLabel(status);
  const color = getColor(status);

  return {
    label,
    color,
  };
}

/* -------------------------------------------------------------------------- */
/*                              HEADER STATUS END                             */
/* -------------------------------------------------------------------------- */

export function mapDeviceCallLogHeaderDto({
  device: { serialNumber },
  status,
}: GetDeviceCallByIdV2QueryResponseApi): DeviceCallLogHeaderDto {
  return {
    subtitle: serialNumber,
    status: mapDeviceCallStatus(status),
  };
}

/* -------------------------------------------------------------------------- */
/*                               HEADER DTO END                               */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                              CALL DETAILS DTO                              */
/* -------------------------------------------------------------------------- */

function getCallDurationInSeconds(number: number): number {
  return Math.floor(number / 1000);
}
function getActionLabel(application: EnumCallApplicationTypeApi): string {
  switch (application) {
    case enumCallApplicationType.DOWNLOAD: {
      return 'synchronization';
    }
    case enumCallApplicationType.HELP_DESK: {
      return 'screenShare';
    }
    default: {
      return '';
    }
  }
}

function getActionColor(
  application: EnumCallApplicationTypeApi,
): 'failed' | 'info' | 'neutral' | 'success' | 'warning' {
  switch (application) {
    case enumCallApplicationType.DOWNLOAD: {
      return 'success';
    }
    case enumCallApplicationType.HELP_DESK: {
      return 'info';
    }
    default: {
      return 'neutral';
    }
  }
}

export function getAction(application: EnumCallApplicationTypeApi): LabelledColored {
  return {
    label: getActionLabel(application),
    color: getActionColor(application),
  };
}

export function mapCallDetailsDto({
  id,
  metadata: { date, logEmitterType, reason, duration, callingApplicationPackageId },
  device: { id: deviceId, reference },
  statusDetail,
}: DeviceCallResourceReadV2Api): CallDetailsDto {
  const callDurationInSeconds = getCallDurationInSeconds(toNumber(duration ?? 0));

  const seconds = callDurationInSeconds % 60;
  const minutes = Math.floor(callDurationInSeconds / 60) % 60;
  const hours = Math.floor(callDurationInSeconds / (60 * 60));

  return {
    id,
    date: date.toISOString(),
    virtualDeviceId: deviceId,
    callingApp: toString(callingApplicationPackageId),
    action: getAction(logEmitterType),
    summary: statusDetail,
    reason: dashString(camelCase(reason)),
    duration: [hours, minutes, seconds].map((time) => time.toString().padStart(2, '0')).join(':'),
    virtualReference: reference,
  };
}

/* -------------------------------------------------------------------------- */
/*                            CALL DETAILS DTO END                            */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                            HARDWARE DETAILS DTO                            */
/* -------------------------------------------------------------------------- */

export function computeChargeLevel(chargeLevel?: string): string {
  if (isEqual(dashString(chargeLevel), dashString())) {
    return dashString();
  }
  return `${chargeLevel?.replaceAll('%', '')}%`;
}

export function mapHardwareDetailsDto(
  { device: { serialNumber }, component }: DeviceCallResourceReadV2Api,
  physicalDeviceItem?: PhysicalDeviceResourceReadApi,
): HardwareDetailsDto {
  const { model, manufacturer, reference } = physicalDeviceItem ?? {};
  return {
    deviceModel: dashString(model),
    serialNumber,
    battteryLevel: computeChargeLevel(component?.battery.chargeLevel),
    connectivity: extractHardwareConnectivity(component?.network),
    manufacturer: dashString(manufacturer).toLowerCase(),
    hardwareReference: dashString(reference),
    batteryStatus: toString(component?.battery.charging).toLowerCase(),
  };
}

function extractHardwareConnectivity(network?: DeviceResourceNetworkDataApi): string {
  if (network) {
    if ((network.bluetooth?.bondedDevices ?? []).length > 0) {
      return 'Bluetooth';
    } else if (network.ethernet) {
      return 'Ethernet';
    } else if (network.wifi) {
      return 'Wifi';
    }
  }
  return dashString();
}

/* -------------------------------------------------------------------------- */
/*                          HARDWARE DETAILS DTO END                          */
/* -------------------------------------------------------------------------- */
export function mapGetDeviceCallLogsByCallIdV1(
  callLogsResponse: GetDeviceCallByIdV2QueryResponseApi,
  { data }: PhysicalDeviceListResponseApi,
): DeviceCallLogDetail {
  const [physicalResponse] = data;
  return {
    header: mapDeviceCallLogHeaderDto(callLogsResponse),
    logLines: callLogsResponse.logsAsString.split('\n'),
    callDetails: mapCallDetailsDto(callLogsResponse),
    hardwareDetails: mapHardwareDetailsDto(callLogsResponse, physicalResponse),
  };
}
